import React, { useState, useMemo, useContext } from "react";
import { Box, CircularProgress, TextField, Typography, Container } from "@mui/material";
import CommonAccordion from "../../custom-components/CommonAccordion";
import MultiAccordian from "./MultiAccordian";
import { generateFSD, userStoryToUseCase } from "../../../services/webServices";
import { useLocation, useNavigate } from "react-router-dom";
import {
  MultipleTestCasesContext,
  TestCasesLoaderContext,
} from "../../main-pages/MultiTestCaseGenerationPage";
import { ReactComponent as GenerateButton } from "../../../images/generateButton.svg";
import SelectionErrorPopUp from "../../popup-components/SelectionerrorPop";
import MessagePopup from "../../popup-components/MessagePopup";
import { handleProgressBar } from "../../custom-components/ProgressBar/ProgressBarWithAPI";
import { LoadingModalContext } from "../../main-pages/ServicePage";

const MultipleUserStoriesComponent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { testCaseLoaderState, setTestCaseLoaderState } = useContext(
    TestCasesLoaderContext
  );
  const {
    multipleUserStories,
    setMultipleUserStories,
    selectedUserStories,
    setSelectedUserStories,
    functionalSpecDocument,
  } = useContext(MultipleTestCasesContext);

  const defaultUserStoriesExpanded =
    multipleUserStories.length > 0 &&
    Object.entries(functionalSpecDocument).length === 0;

  const [isEditing, setIsEditing] = useState(null);
  const [editingValue, setEditingValue] = useState({});
  const [noneSelectionPopOpen, setNoneSelectionPopOpen] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const {setLoadingState} = useContext(LoadingModalContext);

  const summaries = useMemo(
    () =>
      multipleUserStories.map((eachUserStory) => {
        return eachUserStory["Title"];
      }),
    [multipleUserStories]
  );

  const userStoriesComponents = multipleUserStories.map(
    (eachUserStory, userStoryIndex) => (
      <Box key={`eachUserStory_${userStoryIndex}`}>

        {/* Title */}
        {isEditing !== userStoryIndex && (
          <Typography
            variant="subtitle2"
            color="black"
            fontWeight="bold"
            sx={{ fontSize: "14px", marginBottom: 0.5 }}
          >
            Title
          </Typography>
        )}
        {isEditing === userStoryIndex ? (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <TextField
              fullWidth
              multiline
              rows={2}
              variant="outlined"
              label="Title"
              name="Title"
              value={editingValue["Title"]}
              onChange={(e) => handleEdittedValue(e, userStoryIndex)}
              sx={{ marginTop: 2, marginRight: 1, marginBottom: 2 }}
              inputProps={{ style: { fontSize: "14px" } }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              "&:hover": { backgroundColor: "white" },
            }}
          >
            <Typography
              variant="body1"
              sx={{ marginBottom: 2, fontSize: "14px" }}
            >
              {eachUserStory["Title"]}
            </Typography>
          </Box>
        )}

        {/* Description */}
        {isEditing !== userStoryIndex && (
          <Typography
            variant="subtitle2"
            color="black"
            fontWeight="bold"
            sx={{ fontSize: "14px", marginBottom: 0.5 }}
          >
            Description
          </Typography>
        )}
        {isEditing === userStoryIndex ? (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <TextField
              fullWidth
              label="Description"
              name="Description"
              multiline
              rows={4}
              variant="outlined"
              value={editingValue["Description"]}
              onChange={(e) => handleEdittedValue(e, userStoryIndex)}
              sx={{ marginTop: 1, marginRight: 1, marginBottom: 2 }}
              inputProps={{ style: { fontSize: "14px" } }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              "&:hover": { backgroundColor: "white" },
            }}
          >
            <Typography
              variant="body1"
              sx={{ fontSize: "14px", marginBottom: 2 }}
            >
              {eachUserStory["Description"]}
            </Typography>
          </Box>
        )}

        {/* Acceptance Criteria */}
        {isEditing !== userStoryIndex && (
          <Typography
            variant="subtitle2"
            color="black"
            fontWeight="bold"
            sx={{ fontSize: "14px", marginBottom: 0.5 }}
          >
            Acceptance Criteria
          </Typography>
        )}
        {isEditing === userStoryIndex ? (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              label="Acceptance Criteria"
              name="Acceptance Criteria"
              value={editingValue["Acceptance Criteria"]}
              onChange={(e) => handleEdittedValue(e, userStoryIndex)}
              sx={{ marginTop: 1, marginRight: 1, marginBottom: 2 }}
              inputProps={{ style: { fontSize: "14px" } }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              "&:hover": { backgroundColor: "white" },
            }}
          >
            <Typography
              variant="body1"
              sx={{ fontSize: "14px", marginBottom: 2 }}
            >
              {Array.isArray(eachUserStory["Acceptance Criteria"])
                ? eachUserStory["Acceptance Criteria"].map((item, index) => (
                  <React.Fragment key={index}>
                    {item}
                    <br />
                  </React.Fragment>
                ))
                : eachUserStory["Acceptance Criteria"]
                  .toString()
                  .split("\n")
                  .map((line, index) => (
                    <React.Fragment key={index}>
                      {line}
                      <br />
                    </React.Fragment>
                  ))}
            </Typography>
          </Box>
        )}

        {/* Additional Information */}
        {isEditing !== userStoryIndex && (
          <Typography
            variant="subtitle2"
            color="black"
            fontWeight="bold"
            sx={{ fontSize: "14px", marginBottom: 0.5 }}
          >
            Additional Information
          </Typography>
        )}
        {isEditing === userStoryIndex ? (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              label="Additional Information"
              name="Additional Information"
              value={editingValue["Additional Information"]}
              onChange={(e) => handleEdittedValue(e, userStoryIndex)}
              sx={{ marginTop: 1, marginRight: 1, marginBottom: 2 }}
              inputProps={{ style: { fontSize: "14px" } }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              "&:hover": { backgroundColor: "white" },
            }}
          >
            <Typography variant="body1" sx={{ fontSize: "14px" }}>
              {Array.isArray(eachUserStory["Additional Information"])
                ? eachUserStory["Additional Information"].map((item, index) => (
                  <React.Fragment key={index}>
                    {item}
                    <br />
                  </React.Fragment>
                ))
                : eachUserStory["Additional Information"]
                  .toString()
                  .split(/\n|(?=\d+\.\s)/g)
                  .map((line, index) => (
                    <React.Fragment key={index}>
                      {line}
                      <br />
                    </React.Fragment>
                  ))}
            </Typography>
          </Box>
        )}
      </Box>
    )
  );


  const handleEdittedValue = (event) => {
    const updatedValue = {
      ...editingValue,
      [event.target.name]: event.target.value,
    };
    setEditingValue(updatedValue);
  };

  const handleGenerateFSD = async () => {
    let progressHandler;
    try {
      progressHandler = handleProgressBar({setLoadingState, totalDurationMs: 21000});
      progressHandler.start("Generating Functional Specification Document");
      if (selectedUserStories.every((eachUserStory) => !eachUserStory)) {
        setPopupMessage("Please select at least one user story.");
        setPopupOpen(true);
        return;
      }

      const filteredUserStories = selectedUserStories.filter(
        (story) => story !== null && story !== undefined
      );

      if (filteredUserStories.length > 3) {
        setPopupMessage("You can select up to three user stories only.");
        setPopupOpen(true);
        return;
      }

      setTestCaseLoaderState("Functional Specification Document");

      const selectedRequestData = selectedUserStories.filter(
        (eachUserStory) => !!eachUserStory
      );

      const functionalSpecDocument = await generateFSD({
        userStory: selectedRequestData,
      });

      if (
        !functionalSpecDocument?.data?.functionalSpec ||
        functionalSpecDocument.data.functionalSpec.length === 0
      ) {
        setPopupMessage("No FSD returned by the server. Please try again.");
        setPopupOpen(true);
        return;
      }
      await progressHandler.finishAndClose();

      navigate("/dashboard/generate-multi/testcases/output", {
        state: {
          userStories: multipleUserStories,
          selectedUserStories: filteredUserStories,
          functionalSpecDocument: functionalSpecDocument.data.functionalSpec,
          multipleUseCases: [],
          selectedMultipleUseCases: [],
          testConditions: {},
          testingTechniques: {},
          multipleTestCases: [],
          selectedMultipleTestCases: [],
        },
      });
    } catch (error) {
      console.error(error);
      const errorMsg =
        error?.response?.data?.Error ||
        "An error occurred while generating FSD.";
      setPopupMessage(errorMsg);
      setPopupOpen(true);
      setLoadingState({open:false, message:"",progress:null});
    } finally {
      setTestCaseLoaderState(null);
      if (progressHandler){
        progressHandler.stop();
      }
       setLoadingState({open:false, message:"",progress:null});
    }
  };

  const buttons = [
    {
      label: "Generate FSD",
      variant: "contained",
      onClick: handleGenerateFSD,
      sx: { textTransform: "none" },
      disabled:
        testCaseLoaderState === "Functional Specification Document" ||
        !selectedUserStories.some((story) => story),
    },
  ];

  return (
    <Container sx={{ ml: -3 }} maxWidth="lg">
      <Box sx={{ height: "100%", width: { xs: '100%', md: 966, lg: 1100 } }}>
        <CommonAccordion
          summary={`User Stories (${multipleUserStories.length})`}
          details={
            <Box>
              <Box sx={{ overflow: "hidden", width: "100%" }}>
                <MultiAccordian
                  summaries={summaries}
                  multiAccoridanDetails={userStoriesComponents}
                  multiAccoridanData={multipleUserStories}
                  setMultiAccordianData={setMultipleUserStories}
                  selectedAccordians={selectedUserStories}
                  setSelectedAccordians={setSelectedUserStories}
                  isEditing={isEditing}
                  setIsEditing={setIsEditing}
                  editingValue={editingValue}
                  setEditingValue={setEditingValue}
                  loaderCheck={!!testCaseLoaderState}
                />
              </Box>
            </Box>
          }
          buttons={buttons}
          type={"output"}
          fullScreenMode={true}
          defaultExpanded={defaultUserStoriesExpanded}
        />
        <SelectionErrorPopUp
          open={noneSelectionPopOpen}
          onClose={() => setNoneSelectionPopOpen(false)}
        />
        <MessagePopup
          modalOpen={popupOpen}
          okayHandler={() => setPopupOpen(false)}
          message={popupMessage}
        />
      </Box>
    </Container>
  );
};

export default MultipleUserStoriesComponent;
